/**
 * PlayerGrid Component
 *
 * Combines Grid component with piece placement visualization.
 * Features:
 * - Renders 10x10 grid with coordinates
 * - Shows placed pieces on the grid
 * - Shows preview of piece being placed
 * - Handles click events for piece placement
 * - Provides visual feedback for valid/invalid placements
 */

import { useState } from 'react';
import Grid from './Grid';
import Piece from './Piece';

const COLUMNS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

/**
 * Convert coordinate string (e.g., 'A1') to position {x, y}
 */
function coordinateToPosition(coordinate) {
  if (!coordinate || typeof coordinate !== 'string') {
    return null;
  }

  const normalized = coordinate.toUpperCase();
  const match = normalized.match(/^([A-J])(\d+)$/);

  if (!match) {
    return null;
  }

  const [, column, row] = match;
  const columnIndex = COLUMNS.indexOf(column);
  const rowIndex = parseInt(row, 10) - 1;

  if (columnIndex === -1 || rowIndex < 0 || rowIndex >= 10) {
    return null;
  }

  return { x: columnIndex, y: rowIndex };
}

export default function PlayerGrid({
  placedPieces = [],
  onCellClick,
  selectedPiece = null,
  previewPosition = null,
  isPreviewValid = true,
  onCellHover,
  onCellLeave,
  onRemovePiece,
  isLocked = false,
  attackedCells = {},
}) {
  return (
    <div className="relative" data-testid="player-grid-container">
      {/* Grid */}
      <Grid
        onCellClick={(coordinate) => {
          if (!isLocked && onCellClick) {
            const position = coordinateToPosition(coordinate);
            if (position) {
              onCellClick(position);
            }
          }
        }}
        onCellHover={onCellHover}
        onCellLeave={onCellLeave}
        attackedCells={attackedCells}
        isClickable={!isLocked}
      />

      {/* Placed Pieces Layer */}
      <div
        className="absolute top-0 left-0 pointer-events-none"
        style={{
          marginTop: '30px', // Account for column labels (24px) + margin (4px) + grid border (2px)
          marginLeft: '41px', // Account for row labels, margins, grid border, and flex layout spacing
          zIndex: 10, // Ensure pieces are above grid cells
        }}
      >
        {placedPieces.map((piece, index) => (
          <Piece
            key={index}
            data-testid={`placed-piece-${index}`}
            type={piece.type}
            position={piece.position}
            rotation={piece.rotation}
            isPlaced={true}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isLocked && onRemovePiece) {
                onRemovePiece(index);
              }
            }}
          />
        ))}
      </div>

      {/* Preview Layer */}
      {!isLocked && previewPosition && selectedPiece && (
        <div
          className="absolute top-0 left-0 pointer-events-none"
          style={{
            marginTop: '30px',
            marginLeft: '41px',
            zIndex: 5, // Below placed pieces but above grid
          }}
        >
          <div
            data-testid="piece-preview"
            className={`${
              isPreviewValid
                ? 'opacity-80'
                : 'opacity-50 bg-red-500 pointer-events-none'
            }`}
          >
            <Piece
              type={selectedPiece.type}
              position={previewPosition}
              rotation={selectedPiece.rotation || 0}
              isPlaced={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
